import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, Activity, Filter, Calendar as CalIcon, User as UserIcon,
  Building2, MessageCircle, PhoneCall, Handshake, FileText, RefreshCw,
  Check, X as XIcon, MapPin, Clock, Users, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import StatCard from "@/components/StatCard";
import { fmtInt, fmtPct, pct } from "@/lib/format";
import {
  listAtividades, createAtividade, updateAtividade, deleteAtividade,
  listClientes, listCondominios, listVendedores, melhorarObservacao,
} from "@/lib/api";
import { useAuth, isAdmin, isVendedora } from "@/lib/auth";

const TIPOS = [
  { key: "contato", label: "Contato", icon: UserIcon, color: "text-slate-400 border-slate-500/40 bg-slate-500/10" },
  { key: "ligacao", label: "Ligação", icon: PhoneCall, color: "text-sky-400 border-sky-500/40 bg-sky-500/10" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  { key: "visita", label: "Visita", icon: MapPin, color: "text-blue-400 border-blue-500/40 bg-blue-500/10" },
  { key: "reuniao", label: "Reunião", icon: Users, color: "text-indigo-400 border-indigo-500/40 bg-indigo-500/10" },
  { key: "proposta", label: "Proposta", icon: FileText, color: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  { key: "follow_up", label: "Follow-up", icon: RefreshCw, color: "text-purple-400 border-purple-500/40 bg-purple-500/10" },
  { key: "fechamento", label: "Fechamento", icon: Handshake, color: "text-emerald-500 border-emerald-500/60 bg-emerald-500/15" },
  { key: "cancelamento", label: "Cancelamento", icon: XIcon, color: "text-red-500 border-red-500/40 bg-red-500/10" },
  { key: "outro", label: "Outro", icon: Activity, color: "text-muted-foreground border-border bg-muted" },
];
const TIPO_MAP = Object.fromEntries(TIPOS.map((t) => [t.key, t]));

const RESULTADOS = ["", "aguardando", "interessado", "negociando", "sem retorno", "fechou", "autorizado", "recusou"];

const PERIODOS = [
  { key: "dia", label: "Diária", days: 1 },
  { key: "semana", label: "Semanal", days: 7 },
  { key: "mes", label: "Mensal", days: 30 },
];

const emptyForm = {
  vendedor: "",
  data: new Date().toISOString().slice(0, 10),
  tipo: "contato",
  cliente_nome: "",
  condominio: "",
  observacao: "",
  resultado: "",
};

const CLIENTE_NONE = "__none__";
const CONDO_NONE = "__none__";

// Formata YYYY-MM-DD para "dd/mmm" — usado no eixo do gráfico e chips.
const shortDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return "—";
  const [y, m, d] = yyyyMmDd.split("-");
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    .replace(".", "");
};

// Constrói os buckets do gráfico conforme o período selecionado.
const buildBuckets = (periodo) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (periodo === "dia") {
    // últimas 7 datas (para dar sensação de tendência mesmo em visão "diária")
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      out.push({ key, label: shortDate(key), start: key, end: key });
    }
    return out;
  }
  if (periodo === "semana") {
    // últimas 8 semanas, chave = segunda-feira ISO
    const out = [];
    const monday = new Date(today);
    const dow = (monday.getDay() + 6) % 7; // seg=0
    monday.setDate(monday.getDate() - dow);
    for (let i = 7; i >= 0; i--) {
      const start = new Date(monday);
      start.setDate(start.getDate() - i * 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      out.push({
        key: start.toISOString().slice(0, 10),
        label: `${shortDate(start.toISOString().slice(0, 10))}`,
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
      });
    }
    return out;
  }
  // mes: últimos 6 meses (chave = YYYY-MM)
  const out = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const nx = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    out.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }).replace(".", ""),
      start: d.toISOString().slice(0, 10),
      end: nx.toISOString().slice(0, 10),
    });
  }
  return out;
};

const bucketOf = (data, periodo) => {
  if (!data) return "";
  if (periodo === "dia") return data;
  if (periodo === "mes") return data.slice(0, 7);
  // semana: encontra segunda-feira daquela data
  const dt = new Date(data);
  const dow = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - dow);
  return dt.toISOString().slice(0, 10);
};

export default function AtividadesVendedorCard() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const vend = isVendedora(user);
  const myVendedor = (user?.vendedor_name || "").trim();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("semana");
  const [vendedorFilter, setVendedorFilter] = useState("todos");
  const [tipoFilter, setTipoFilter] = useState("todos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm, vendedor: myVendedor });
  const [saving, setSaving] = useState(false);
  // Estado do assistente de IA (Claude Sonnet 4.6) — melhora a observação em rascunho.
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOriginal, setAiOriginal] = useState(null); // guarda o rascunho pra desfazer
  const [clientes, setClientes] = useState([]);
  const [condominios, setCondominios] = useState([]);
  const [vendedoresLista, setVendedoresLista] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ativ, cli, condos] = await Promise.all([
        listAtividades(),
        listClientes().catch(() => []),
        listCondominios().catch(() => []),
      ]);
      setItems(Array.isArray(ativ) ? ativ : []);
      setClientes(Array.isArray(cli) ? cli : []);
      setCondominios(Array.isArray(condos) ? condos : []);
    } catch (err) {
      console.error("Erro ao carregar atividades:", err);
      toast.error("Erro ao carregar atividades");
    } finally { setLoading(false); }
  }, []);

  const loadVendedoresLista = useCallback(async () => {
    try { setVendedoresLista(await listVendedores()); }
    catch (err) { console.debug("listVendedores indisponível para vendedora:", err?.message); }
  }, []);

  useEffect(() => {
    load();
    if (!vend) loadVendedoresLista();
  }, [load, loadVendedoresLista, vend]);

  // Se vendedora, trava filtro no nome dela.
  useEffect(() => {
    if (vend && myVendedor) setVendedorFilter(myVendedor);
  }, [vend, myVendedor]);

  const vendedoresOptions = useMemo(() => {
    const set = new Set();
    for (const a of items) if ((a.vendedor || "").trim()) set.add(a.vendedor.trim());
    for (const v of vendedoresLista) if (v?.vendedor_name) set.add(v.vendedor_name);
    return [...set].sort();
  }, [items, vendedoresLista]);

  const filtered = useMemo(() => {
    const buckets = buildBuckets(periodo);
    const startCutoff = buckets[0]?.start || "";
    return items.filter((a) => {
      if (vendedorFilter !== "todos" && (a.vendedor || "") !== vendedorFilter) return false;
      if (tipoFilter !== "todos" && (a.tipo || "") !== tipoFilter) return false;
      if (startCutoff && (a.data || "") < startCutoff) return false;
      return true;
    });
  }, [items, periodo, vendedorFilter, tipoFilter]);

  // Ordena descendente por data uma vez por mudança de `filtered`, evitando
  // criar/reordenar array em todo render (recomendação de perf da revisão).
  const filteredSorted = useMemo(
    () => [...filtered].sort((a, b) => (b.data || "").localeCompare(a.data || "")),
    [filtered]
  );

  const kpis = useMemo(() => {
    const total = filtered.length;
    const fechamentos = filtered.filter((a) => a.tipo === "fechamento").length;
    const propostas = filtered.filter((a) => a.tipo === "proposta").length;
    const contatos = filtered.filter(
      (a) => a.tipo === "contato" || a.tipo === "ligacao" || a.tipo === "whatsapp" || a.tipo === "visita"
    ).length;
    const conv = pct(fechamentos, propostas);
    return { total, fechamentos, propostas, contatos, conv };
  }, [filtered]);

  // Dados do chart agrupado por bucket + tipo
  const chartData = useMemo(() => {
    const buckets = buildBuckets(periodo);
    const map = Object.fromEntries(
      buckets.map((b) => [b.key, { label: b.label, Contatos: 0, Propostas: 0, Fechamentos: 0, Outros: 0 }])
    );
    for (const a of filtered) {
      const bk = bucketOf(a.data, periodo);
      if (!(bk in map)) continue;
      if (a.tipo === "proposta") map[bk].Propostas += 1;
      else if (a.tipo === "fechamento") map[bk].Fechamentos += 1;
      else if (["contato", "ligacao", "whatsapp", "visita"].includes(a.tipo)) map[bk].Contatos += 1;
      else map[bk].Outros += 1;
    }
    return buckets.map((b) => map[b.key]);
  }, [filtered, periodo]);

  // Ranking por vendedor no período
  const rankingVend = useMemo(() => {
    const map = {};
    for (const a of filtered) {
      const v = (a.vendedor || "").trim() || "Sem vendedor";
      map[v] = (map[v] || { nome: v, total: 0, fechamentos: 0 });
      map[v].total += 1;
      if (a.tipo === "fechamento") map[v].fechamentos += 1;
    }
    const arr = Object.values(map).sort((a, b) => b.total - a.total);
    const max = arr[0]?.total || 1;
    return arr.map((v) => ({ ...v, pct: (v.total / max) * 100 }));
  }, [filtered]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, vendedor: vend ? myVendedor : "" });
    setAiOriginal(null);
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm, ...item });
    setAiOriginal(null);
    setOpen(true);
  };

  const handleMelhorar = async () => {
    const rascunho = (form.observacao || "").trim();
    if (rascunho.length < 3) {
      toast.error("Escreva um rascunho antes de pedir para melhorar");
      return;
    }
    setAiLoading(true);
    try {
      const contextoParts = [];
      if (form.tipo) contextoParts.push(`Tipo: ${TIPO_MAP[form.tipo]?.label || form.tipo}`);
      if (form.cliente_nome) contextoParts.push(`Cliente: ${form.cliente_nome}`);
      if (form.condominio) contextoParts.push(`Condomínio: ${form.condominio}`);
      if (form.resultado) contextoParts.push(`Resultado: ${form.resultado}`);
      const { observacao_melhorada } = await melhorarObservacao({
        observacao: rascunho,
        contexto: contextoParts.join(" · "),
      });
      setAiOriginal(rascunho);
      setF("observacao", observacao_melhorada);
      toast.success("Observação reescrita pelo assistente — desfaça se preferir o rascunho");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não consegui contatar o assistente");
    } finally {
      setAiLoading(false);
    }
  };

  const handleDesfazerMelhoria = () => {
    if (!aiOriginal) return;
    setF("observacao", aiOriginal);
    setAiOriginal(null);
    toast.info("Rascunho original restaurado");
  };

  const handleSave = async () => {
    if (!form.vendedor.trim()) { toast.error("Informe o vendedor"); return; }
    if (!form.data) { toast.error("Informe a data"); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateAtividade(editing.id, form);
        toast.success("Atividade atualizada");
      } else {
        await createAtividade(form);
        toast.success("Atividade registrada");
      }
      setOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar atividade");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta atividade?")) return;
    try {
      await deleteAtividade(id);
      toast.success("Atividade removida");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-5" data-testid="atividades-vendedor-card">
      {/* Header + toggle */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-semibold text-lg">Relatório dos vendedores</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Cada contato, visita ou proposta que a equipe registra. Alterne entre diário, semanal e mensal para ver o ritmo do time.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-border overflow-hidden shrink-0" data-testid="periodo-toggle">
              {PERIODOS.map((p) => {
                const active = periodo === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPeriodo(p.key)}
                    data-testid={`periodo-${p.key}`}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      active ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <Button onClick={openCreate} data-testid="btn-add-atividade" size="sm">
              <Plus className="w-4 h-4 mr-1.5" /> Nova ação
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          <Select value={vendedorFilter} onValueChange={setVendedorFilter} disabled={vend}>
            <SelectTrigger data-testid="filter-atividade-vendedor">
              <div className="flex items-center gap-2">
                <UserIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os vendedores</SelectItem>
              {vendedoresOptions.map((v) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={tipoFilter} onValueChange={setTipoFilter}>
            <SelectTrigger data-testid="filter-atividade-tipo">
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
            <CalIcon className="w-3.5 h-3.5" />
            {loading ? "Carregando…" : `${filtered.length} ação(ões) no período`}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard testId="kpi-ativ-total" label="Ações registradas" value={fmtInt(kpis.total)} icon={Activity} />
          <StatCard testId="kpi-ativ-contatos" label="Contatos / abordagens" value={fmtInt(kpis.contatos)} icon={PhoneCall} tone="default" />
          <StatCard testId="kpi-ativ-propostas" label="Propostas enviadas" value={fmtInt(kpis.propostas)} icon={FileText} tone="warning" />
          <StatCard testId="kpi-ativ-fechamentos" label="Fechamentos" value={fmtInt(kpis.fechamentos)} icon={Handshake} tone="success" />
          <StatCard
            testId="kpi-ativ-conv"
            label="Proposta → Fechamento"
            value={fmtPct(kpis.conv)}
            icon={Check}
            tone={kpis.conv >= 50 ? "success" : kpis.conv >= 25 ? "warning" : "danger"}
          />
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-atividades">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="font-heading font-semibold">Ritmo de ações por {periodo === "dia" ? "dia" : periodo === "semana" ? "semana" : "mês"}</h4>
            <p className="text-xs text-muted-foreground">Contatos, propostas e fechamentos agrupados.</p>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Contatos" stackId="acoes" fill="hsl(var(--chart-3))" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Propostas" stackId="acoes" fill="hsl(var(--chart-4))" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Fechamentos" stackId="acoes" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Outros" stackId="acoes" fill="hsl(var(--muted))" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ranking por vendedor no período (admin/tecnico) */}
      {!vend && rankingVend.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-atividades">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-primary" />
            <h4 className="font-heading font-semibold">Ranking de ações por vendedor</h4>
            <Badge variant="outline" className="ml-auto">{PERIODOS.find((p) => p.key === periodo)?.label}</Badge>
          </div>
          <div className="space-y-2">
            {rankingVend.map((v) => (
              <div key={v.nome} className="flex items-center gap-3 text-sm">
                <span className="w-40 truncate font-medium">{v.nome}</span>
                <div className="flex-1"><Progress value={v.pct} className="h-2" /></div>
                <span className="tabular-nums font-mono text-muted-foreground w-20 text-right">{fmtInt(v.total)} ações</span>
                <Badge variant="outline" className="text-emerald-500 border-emerald-500/40 text-[10px] tabular-nums w-20 justify-center">
                  {fmtInt(v.fechamentos)} fech.
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline / tabela detalhada */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <h4 className="font-heading font-semibold">Últimas ações registradas</h4>
          </div>
          <Badge variant="outline">{filtered.length} ação(ões)</Badge>
        </div>
        <div className="thin-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[110px]">Data</TableHead>
                <TableHead>Vendedor</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Cliente / Condomínio</TableHead>
                <TableHead className="min-w-[280px]">Observação</TableHead>
                <TableHead>Resultado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Carregando…</TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                  Nenhuma ação neste período. Clique em <strong>“Nova ação”</strong> para registrar a primeira.
                </TableCell></TableRow>
              )}
              {[...filteredSorted].map((a) => {
                const t = TIPO_MAP[a.tipo] || TIPO_MAP.outro;
                const Icon = t.icon;
                const canEdit = admin || (vend && a.vendedor === myVendedor);
                return (
                  <TableRow key={a.id} data-testid={`atividade-row-${a.id}`}>
                    <TableCell className="tabular-nums text-sm font-mono text-muted-foreground">
                      {shortDate(a.data)}
                    </TableCell>
                    <TableCell className="font-medium">{a.vendedor}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[11px] gap-1 ${t.color}`}>
                        <Icon className="w-3 h-3" /> {t.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.cliente_nome && (
                        <div className="flex items-center gap-1 text-foreground">
                          <UserIcon className="w-3 h-3 text-muted-foreground" /> {a.cliente_nome}
                        </div>
                      )}
                      {a.condominio && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Building2 className="w-3 h-3" /> {a.condominio}
                        </div>
                      )}
                      {!a.cliente_nome && !a.condominio && <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm max-w-[380px]">
                      {a.observacao ? (
                        <span className="text-foreground/90 leading-snug whitespace-pre-line block">{a.observacao}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Sem observação</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {a.resultado ? (
                        <Badge variant="outline" className="capitalize text-[10px]">{a.resultado}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`btn-edit-atividade-${a.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDelete(a.id)} data-testid={`btn-delete-atividade-${a.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dialog-atividade">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar ação" : "Registrar nova ação"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Vendedor *</Label>
              {vend ? (
                <Input value={myVendedor} disabled data-testid="input-atividade-vendedor" />
              ) : (
                <Select value={form.vendedor} onValueChange={(v) => setF("vendedor", v)}>
                  <SelectTrigger data-testid="select-atividade-vendedor"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {vendedoresOptions.map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Data *</Label>
              <Input type="date" value={form.data} onChange={(e) => setF("data", e.target.value)} data-testid="input-atividade-data" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de ação</Label>
              <Select value={form.tipo} onValueChange={(v) => setF("tipo", v)}>
                <SelectTrigger data-testid="select-atividade-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Resultado (opcional)</Label>
              <Select value={form.resultado || "__none__"} onValueChange={(v) => setF("resultado", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="select-atividade-resultado"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sem resultado ainda</SelectItem>
                  {RESULTADOS.filter(Boolean).map((r) => (
                    <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5 text-muted-foreground" />Cliente (opcional)</Label>
              <Select
                value={form.cliente_nome || CLIENTE_NONE}
                onValueChange={(v) => setF("cliente_nome", v === CLIENTE_NONE ? "" : v)}
              >
                <SelectTrigger data-testid="select-atividade-cliente"><SelectValue placeholder="Selecione ou deixe em branco" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={CLIENTE_NONE}>Sem cliente vinculado</SelectItem>
                  {[...new Set(clientes.map((c) => c.nome_cliente).filter(Boolean))].sort().map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" />Condomínio (opcional)</Label>
              <Select
                value={form.condominio || CONDO_NONE}
                onValueChange={(v) => setF("condominio", v === CONDO_NONE ? "" : v)}
              >
                <SelectTrigger data-testid="select-atividade-condominio"><SelectValue placeholder="Selecione ou deixe em branco" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={CONDO_NONE}>Sem condomínio vinculado</SelectItem>
                  {condominios.map((c) => (
                    <SelectItem key={c.id} value={c.condominio}>{c.condominio}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="mb-0">Observações *</Label>
                <div className="flex items-center gap-1.5">
                  {aiOriginal && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDesfazerMelhoria}
                      className="h-7 text-[11px] text-muted-foreground hover:text-foreground"
                      data-testid="btn-desfazer-ia"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" /> Desfazer
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleMelhorar}
                    disabled={aiLoading || (form.observacao || "").trim().length < 3}
                    className="h-7 text-[11px] gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
                    data-testid="btn-melhorar-observacao"
                  >
                    {aiLoading ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Reescrevendo…</>
                    ) : (
                      <><Sparkles className="w-3 h-3" /> Melhorar com IA</>
                    )}
                  </Button>
                </div>
              </div>
              <Textarea
                value={form.observacao}
                onChange={(e) => { setF("observacao", e.target.value); if (aiOriginal) setAiOriginal(null); }}
                placeholder="O que foi combinado? Qual o próximo passo? Ex: Cliente pediu proposta Premium com desconto — retornar quinta 14h."
                rows={4}
                data-testid="input-atividade-observacao"
              />
              <p className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                <span>{form.observacao.length} caractere(s) · seja específica para ajudar a equipe a acompanhar</span>
                {aiOriginal && (
                  <span className="text-primary flex items-center gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> versão da IA aplicada
                  </span>
                )}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-atividade">
              {saving ? "Salvando…" : editing ? "Salvar alterações" : "Registrar ação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
